import "@tento-ui/components/list/list-cell-layout";

import type { LonaWebComponent } from "@tento-ui/component";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { AngleRightIcon, LonaIcons } from "@tento-ui/ui/icons";
import { ListCellLayout } from "@tento-ui/components/list/list-cell-layout";
import { Typography } from "@tento-ui/ui/typography";

/**
 * Shared CTA row for NUX onboarding cards.
 *
 * Mirrors the sidebar's quick-action rows (`HomeQuickActions.$makeRow` in
 * `lib/core/sidebar/sidebar-home.ts`): an icon tile + label in a
 * `std-list-cell-layout`. Adds a centered chevron accessory and a permanently
 * lit background so the row reads as an always-active CTA. The icon idles muted
 * and colorizes on hover — row hover is forwarded to the icon's `hover`
 * attribute (sidebar parity), since the icon's own `:host(:hover)` only fires
 * over the small tile.
 */
export function $makeNuxCtaRow(opts: {
  $icon: LonaWebComponent;
  label: string;
  onClick: () => void;
}): ListCellLayout {
  const { $icon, label, onClick } = opts;
  $icon.assignStyles({ marginLeft: "0px" });

  const $content = Typography.h1(label, "p");
  $content.assignStyles({
    marginLeft: "0px",
    // `--cell-align: center` handles vertical positioning, so no baseline hack.
    "--margin-top": "0px",
    color: "var(--text-sec)",
    fontWeight: "500",
    cursor: "pointer",
  });

  const $chevron = LonaIcons.make(AngleRightIcon, {
    sizePx: 14,
    color: "var(--text-ter)",
  });

  const $cell = ListCellLayout.makeWith({
    $icon,
    $content,
    $accessory: $chevron,
  });
  $cell.assignStyles({
    // Center icon + label + chevron (the accessory otherwise top-aligns).
    "--cell-align": "center",
    // Permanent hover: keep the row background lit.
    "--list-cell-background-color": "var(--bg-hover)",
    "--icon-button-dimension": "20px",
    "--list-cell-padding": "8px 8px",
    cursor: "pointer",
  });

  // Colorize the muted icon while hovering anywhere on the row.
  $cell.addEventListener("pointerenter", () =>
    $icon.toggleAttribute("hover", true),
  );
  $cell.addEventListener("pointerleave", () =>
    $icon.toggleAttribute("hover", false),
  );
  GESTURE_MANAGER.addPointerEvent($cell, { onClick });

  return $cell;
}
